import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Star, Plus, Trash2, Search, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpecialPrices() {
  const [prices, setPrices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCode, setFilterCode] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);

  // Add form
  const [showForm, setShowForm] = useState(false);
  const [formErpCustomer, setFormErpCustomer] = useState("");
  const [formErpCode, setFormErpCode] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [saving, setSaving] = useState(false);

  // Load clients for helper dropdown
  useEffect(() => {
    axios.get(`${API}/admin/users`, { params: { limit: 500 } })
      .then((res) => setClients(res.data || []))
      .catch(() => {});
  }, []);

  const fetchPrices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCode) params.erp_customer_code = filterCode;
      const res = await axios.get(`${API}/admin/prices/special`, { params });
      setPrices(res.data || []);
    } catch {
      toast.error("Error cargando precios especiales");
    } finally {
      setLoading(false);
    }
  }, [filterCode]);

  useEffect(() => { fetchPrices(); }, [fetchPrices]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filtered = search
    ? prices.filter(
        (p) =>
          (p.erp_code || "").toLowerCase().includes(search) ||
          (p.erp_customer_code || "").toLowerCase().includes(search)
      )
    : prices;

  const handleSave = async () => {
    if (!formErpCustomer.trim() || !formErpCode.trim() || !formPrice) {
      toast.error("Completa todos los campos");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/prices/special`, {
        erp_customer_code: formErpCustomer.trim(),
        erp_code: formErpCode.trim(),
        price: parseFloat(formPrice),
      });
      toast.success("Precio especial guardado");
      setFormErpCustomer("");
      setFormErpCode("");
      setFormPrice("");
      setShowForm(false);
      fetchPrices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (erp_customer_code, erp_code) => {
    try {
      await axios.delete(`${API}/admin/prices/special`, { params: { erp_customer_code, erp_code } });
      setPrices((prev) =>
        prev.filter(
          (p) => !(p.erp_customer_code === erp_customer_code && p.erp_code === erp_code)
        )
      );
      toast.success("Eliminado");
    } catch {
      toast.error("Error eliminando");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/prices/special/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importados: ${res.data.upserted} precios (${res.data.errors} errores)`);
      fetchPrices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en la importación");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Unique erp_customer_codes from clients
  const uniqueErpCodes = [...new Set(clients.map((c) => c.erp_customer_code).filter(Boolean))];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="special-prices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Precios Especiales</h1>
              <p className="text-slate-500">{prices.length} precios configurados</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <input type="file" accept=".csv" className="hidden" onChange={handleUpload} data-testid="special-csv-input" />
              <span className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium text-white ${uploading ? 'bg-amber-400' : 'bg-amber-500 hover:bg-amber-600'} transition-colors`}>
                <Upload className="w-4 h-4" />
                {uploading ? "Importando..." : "Importar CSV"}
              </span>
            </label>
            <Button
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              onClick={() => setShowForm(true)}
              data-testid="add-special-price-btn"
            >
              <Plus className="w-4 h-4" /> Nuevo
            </Button>
          </div>
        </div>

        {/* Help */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-sm text-amber-800">
            Precios por <strong>código cliente A3</strong> (erp_customer_code). Varios usuarios pueden compartir el mismo código A3 (ej: cadena Rustic con 3 locales).<br />
            <strong>CSV:</strong> erp_customer_code,erp_code,price
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por código..."
              className="pl-10"
              data-testid="special-price-search"
            />
          </div>
          <select
            value={filterCode}
            onChange={(e) => setFilterCode(e.target.value)}
            className="h-10 px-3 border border-slate-200 rounded-md text-sm"
            data-testid="special-price-filter"
          >
            <option value="">Todos los clientes A3</option>
            {uniqueErpCodes.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </div>

        {/* Add form modal */}
        {showForm && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Nuevo Precio Especial</h3>
                <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-200 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Código cliente A3</label>
                  <Input
                    value={formErpCustomer}
                    onChange={(e) => setFormErpCustomer(e.target.value)}
                    placeholder="CLI001"
                    list="erp-codes-list"
                    data-testid="special-erp-customer-input"
                  />
                  <datalist id="erp-codes-list">
                    {uniqueErpCodes.map((code) => (
                      <option key={code} value={code} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Código producto ERP</label>
                  <Input
                    value={formErpCode}
                    onChange={(e) => setFormErpCode(e.target.value)}
                    placeholder="ART-001"
                    data-testid="special-erp-code-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Precio</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formPrice}
                    onChange={(e) => setFormPrice(e.target.value)}
                    placeholder="0.00"
                    data-testid="special-price-input"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-3">
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-special-price-btn"
                >
                  {saving ? "..." : "Guardar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Precios Especiales</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center py-12 text-slate-500">
                {search || filterCode ? "Sin resultados" : "No hay precios especiales configurados"}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código Cliente A3</TableHead>
                      <TableHead>Código Producto ERP</TableHead>
                      <TableHead>Precio</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((p, i) => (
                      <TableRow key={`${p.erp_customer_code}-${p.erp_code}-${i}`} data-testid={`special-price-row-${i}`}>
                        <TableCell className="font-mono text-sm">{p.erp_customer_code}</TableCell>
                        <TableCell className="font-mono text-sm">{p.erp_code}</TableCell>
                        <TableCell className="font-semibold">{Number(p.price).toFixed(2)}€</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(p.erp_customer_code, p.erp_code)}
                            data-testid={`delete-special-price-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
